import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, MousePointerClick, ShoppingCart,
  Wallet, Globe, Layers, Repeat, BarChart3, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Private founder-affiliate analytics panel.
 *
 * Props:
 *   endpoint  — full URL to the analytics endpoint, e.g.
 *               `${API}/api/influencer/analytics` (influencer-facing) or
 *               `${API}/api/admin/influencers/${id}/analytics` (admin).
 *   withCredentials — set true when using cookie auth (influencer side);
 *                     false when using Authorization header (admin side).
 *   headers   — optional Authorization header for admin calls.
 *
 * Privacy: the parent component is responsible for not rendering this in
 * a public context. This component never fetches data for any affiliate
 * OTHER than the one its endpoint URL points to.
 */
export default function FounderAffiliateAnalytics({ endpoint, withCredentials = false, headers = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productWindow, setProductWindow] = useState('30d');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(endpoint, { withCredentials, headers });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, withCredentials, headers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  if (error) {
    return <div className="text-sm text-red-400 p-4 border border-red-500/30 rounded-lg" data-testid="analytics-error">{error}</div>;
  }
  if (!data) return null;

  const s30 = data.summary['30d'];
  const trend = data.trend_30d_vs_prior;

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      {/* 30-day-vs-prior trend tiles */}
      <div>
        <div className="text-xs uppercase tracking-wide font-bold text-[#00F0FF] mb-2 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> Last 30 days vs prior 30
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <TrendTile icon={MousePointerClick} label="Clicks"
                     value={s30.clicks} deltaPct={trend.clicks_pct} accent="#00F0FF" />
          <TrendTile icon={ShoppingCart} label="Conversions"
                     value={s30.conversions} deltaPct={trend.conversions_pct} accent="#a78bfa" />
          <TrendTile icon={Wallet} label="Commission"
                     value={`$${s30.commission_earned.toFixed(2)}`}
                     deltaPct={trend.commission_pct} accent="#34d399" />
          <TrendTile icon={BarChart3} label="Conv. rate"
                     value={`${s30.conv_rate_pct.toFixed(1)}%`}
                     deltaPctPoints={trend.conv_rate_pct_pts} accent="#f59e0b" />
        </div>
      </div>

      {/* Revenue split: base 1% vs stacked */}
      <Card title="Revenue split — base vs stacked" icon={Layers}>
        <RevenueSplitBar data={data.revenue_split} />
      </Card>

      {/* Daily funnel sparkbars */}
      <Card title="Daily clicks → conversions (last 30d)" icon={BarChart3}>
        <DailyFunnelChart rows={data.daily_funnel_30d} />
      </Card>

      {/* Top products */}
      <Card title="Top products driven" icon={ShoppingCart}>
        <div className="flex gap-1.5 mb-3" data-testid="top-products-windows">
          {['30d', '90d', 'all'].map((w) => (
            <button key={w} onClick={() => setProductWindow(w)}
                    data-testid={`window-${w}`}
                    className={`px-2.5 py-1 text-xs rounded-md transition ${
                      productWindow === w
                        ? 'bg-[#00F0FF] text-black font-semibold'
                        : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                    }`}>
              {w === 'all' ? 'All-time' : w.toUpperCase()}
            </button>
          ))}
        </div>
        {(data.top_products[productWindow] || []).length === 0 ? (
          <p className="text-xs text-neutral-500 italic">No product-level commissions in this window yet — stacked rows appear here once a buyer purchases a product with a creator affiliate program.</p>
        ) : (
          <div className="space-y-1.5">
            {data.top_products[productWindow].slice(0, 5).map((p) => (
              <div key={p.product_id} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex-1 min-w-0 truncate text-neutral-200">{p.name}</div>
                <div className="text-xs text-neutral-500">{p.sales_count} sale{p.sales_count !== 1 ? 's' : ''}</div>
                <div className="text-sm font-semibold text-emerald-400 w-20 text-right">
                  ${p.commission_total.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top countries */}
        <Card title="Top buyer countries" icon={Globe}>
          {data.top_countries.length === 0 ? (
            <p className="text-xs text-neutral-500 italic">No clicks yet.</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_countries.slice(0, 6).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-300">{c.country || 'Unknown'}</span>
                  <span className="text-xs text-neutral-500">{c.clicks} clicks</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Source breakdown */}
        <Card title="Source breakdown" icon={Repeat}>
          {Object.keys(data.source_breakdown || {}).length === 0 ? (
            <p className="text-xs text-neutral-500 italic">No clicks recorded yet.</p>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(data.source_breakdown).map(([src, v]) => (
                <div key={src} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-300">
                    {src === 'branded_slug' ? 'Branded URL (theailedger.ai/slug)'
                      : src === 'r_redirect' ? 'Legacy /r/ link' : src}
                  </span>
                  <span className="text-xs text-neutral-500">{v.clicks} clicks</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* MRR */}
      <Card title="Subscription MRR" icon={Repeat}>
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Active subs (35d)" value={data.mrr.active_subscriptions} />
          <MiniStat label="Monthly recurring" value={`$${data.mrr.mrr_commission.toFixed(2)}`} accent="#34d399" />
          <MiniStat label="Subs lifetime" value={`$${data.mrr.subscription_lifetime_commission.toFixed(2)}`} accent="#a78bfa" />
        </div>
      </Card>

      <p className="text-[10px] text-neutral-600 text-center pt-2">
        Private analytics — visible only to the admin and to {data.summary['30d'].clicks ? 'this' : 'the assigned'} affiliate. Never displayed on public leaderboards.
      </p>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide font-bold text-neutral-400 mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#00F0FF]" />}
        {title}
      </div>
      {children}
    </div>
  );
}

function TrendTile({ icon: Icon, label, value, deltaPct, deltaPctPoints, accent }) {
  const delta = deltaPct !== undefined ? deltaPct : deltaPctPoints;
  const isUp = delta > 0;
  const isFlat = delta === 0;
  const Arrow = isUp ? TrendingUp : TrendingDown;
  return (
    <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-3">
      <Icon className="w-3.5 h-3.5 mb-1.5" style={{ color: accent }} />
      <div className="text-[10px] uppercase text-neutral-500 tracking-wide">{label}</div>
      <div className="text-xl font-bold mt-0.5" style={{ color: accent }}>{value}</div>
      {!isFlat && (
        <div className={`text-[11px] mt-1 flex items-center gap-0.5 font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
          <Arrow className="w-3 h-3" />
          {isUp ? '+' : ''}{delta}{deltaPctPoints !== undefined ? 'pts' : '%'}
        </div>
      )}
    </div>
  );
}

function RevenueSplitBar({ data }) {
  const base = data.founder_base || { count: 0, total: 0, pct: 0 };
  const stacked = data.creator_stacked || { count: 0, total: 0, pct: 0 };
  const total = base.total + stacked.total;
  if (total === 0) {
    return <p className="text-xs text-neutral-500 italic">No commissions yet. Once your buyers start purchasing, the base 1% (from TheAILedger&apos;s cut) will appear here, plus any stacked creator commissions when buyers don&apos;t use other affiliate links.</p>;
  }
  return (
    <>
      <div className="flex h-3 rounded-full overflow-hidden mb-3">
        {base.total > 0 && (
          <div className="bg-[#00F0FF]" style={{ width: `${base.pct}%` }} title={`Base: $${base.total.toFixed(2)}`} />
        )}
        {stacked.total > 0 && (
          <div className="bg-emerald-400" style={{ width: `${stacked.pct}%` }} title={`Stacked: $${stacked.total.toFixed(2)}`} />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#00F0FF]" />
            <span className="text-neutral-400 text-xs uppercase">Base (1%)</span>
          </div>
          <div className="text-base font-semibold text-white mt-0.5">${base.total.toFixed(2)}</div>
          <div className="text-[11px] text-neutral-500">{base.count} commission{base.count !== 1 ? 's' : ''} · {base.pct}%</div>
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-neutral-400 text-xs uppercase">Stacked</span>
          </div>
          <div className="text-base font-semibold text-white mt-0.5">${stacked.total.toFixed(2)}</div>
          <div className="text-[11px] text-neutral-500">{stacked.count} commission{stacked.count !== 1 ? 's' : ''} · {stacked.pct}%</div>
        </div>
      </div>
    </>
  );
}

function DailyFunnelChart({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div className="flex items-end gap-0.5 h-20" data-testid="daily-funnel">
      {rows.map((r) => {
        const h = (r.clicks / max) * 100;
        const ch = r.conversions > 0 ? Math.max(8, (r.conversions / max) * 100) : 0;
        return (
          <div key={r.date} className="flex-1 flex flex-col-reverse min-w-0 group relative"
               title={`${r.date}: ${r.clicks} clicks, ${r.conversions} conversions (${r.conv_rate}%)`}>
            {r.clicks > 0 && (
              <div className="w-full bg-[#00F0FF]/40 rounded-t-sm" style={{ height: `${h}%` }} />
            )}
            {r.conversions > 0 && (
              <div className="w-full bg-emerald-400 rounded-t-sm absolute bottom-0 left-0" style={{ height: `${ch}%` }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function MiniStat({ label, value, accent = '#ffffff' }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="text-base font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
